import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, new_password: password })
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => navigate('/auth'), 3000);
      } else {
        const data = await response.json();
        setError(data.detail || 'Ein Fehler ist aufgetreten');
      }
    } catch (err) {
      setError('Verbindungsfehler. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  if (!resetToken) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center py-12 bg-[hsl(var(--surface-2))]">
        <div className="container mx-auto px-4 max-w-md">
          <Card className="card-shadow">
            <CardContent className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-[hsl(var(--destructive))] mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Ungültiger Link</h2>
              <p className="text-[hsl(var(--muted-foreground))] mb-4">
                Dieser Reset-Link ist ungültig oder abgelaufen.
              </p>
              <Link to="/forgot-password">
                <Button data-testid="reset-request-new-link">
                  Neuen Link anfordern
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-12 bg-[hsl(var(--surface-2))]">
      <div className="container mx-auto px-4 max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Card className="card-shadow">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Neues Passwort setzen</CardTitle>
              <CardDescription>
                Geben Sie Ihr neues Passwort ein.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {success ? (
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 bg-[hsl(var(--chart-2)/0.1)] rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle className="w-8 h-8 text-[hsl(var(--chart-2))]" />
                  </div>
                  <div>
                    <p className="font-medium text-[hsl(var(--foreground))]">
                      Passwort erfolgreich geändert!
                    </p>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                      Sie werden automatisch zum Login weitergeleitet...
                    </p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="new-password">Neues Passwort</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                      <Input
                        id="new-password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10"
                        placeholder="Neues Passwort"
                        required
                        minLength={6}
                        data-testid="reset-password-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Passwort bestätigen</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                      <Input
                        id="confirm-password"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-10"
                        placeholder="Passwort bestätigen"
                        required
                        data-testid="reset-confirm-password-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading}
                    data-testid="reset-password-submit-button"
                  >
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                    Passwort zurücksetzen
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
